import { useState } from "react";
import { CalendarRange, Loader2, Plus, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { dayLabel, newDay, nextSchoolDay, toISO } from "./dates";

const selCls = "bg-slate-800 border border-slate-600 rounded-md h-9 px-2 text-sm text-slate-200 max-w-full";
const KIND = { lesson: "Lesson", drill: "Drill", task: "Block Task", checkpoint: "Checkpoint", midtask: "Mid-Unit Task", midtest: "Mid-Unit Test", unittask: "End of Unit Task", unittest: "End of Unit Test" };
const UNIT_KINDS = ["midtask", "midtest", "unittask", "unittest"];
const SHORT = { design: "Design", docs: "Docs", email: "Email", sheets: "Sheets", slides: "Slides" };

export const itemLabel = (it) => {
  const kind = KIND[it.kind] || it.kind;
  const where = it.block ? `B${it.block}` : "Unit";
  return `${SHORT[it.track] || it.track} · ${where} · ${kind}${it.title && it.title !== kind ? `: ${it.title}` : ""}`;
};

function DayRow({ day, idx, catalog, onChange, onRemove }) {
  const [track, setTrack] = useState(catalog[0]?.id || "docs");
  const [block, setBlock] = useState(1);
  const t = catalog.find((c) => c.id === track);
  const items = t?.blocks.find((b) => b.index === block)?.items || [];
  const add = (id) => {
    const it = items.find((x) => x.id === id);
    if (!it || day.items.some((x) => x.id === id)) return;
    onChange({ ...day, items: [...day.items, { track, block, kind: it.kind, id: it.id, title: it.title }] });
  };
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2" data-testid={`lp-day-row-${idx}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-[#a5b4fc]" data-testid={`lp-day-label-${idx}`}>{dayLabel(day.date)}</p>
        <button type="button" data-testid={`lp-day-remove-${idx}`} onClick={onRemove} className="text-slate-500 hover:text-[#E11D48]"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex flex-wrap gap-2">
        <select data-testid={`lp-day-track-${idx}`} value={track} onChange={(e) => setTrack(e.target.value)} className={selCls}>
          {catalog.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <select data-testid={`lp-day-block-${idx}`} value={block} onChange={(e) => setBlock(Number(e.target.value))} className={selCls}>
          {(t?.blocks || []).map((b) => <option key={b.index} value={b.index}>{b.label || `Block ${b.index}`}</option>)}
        </select>
        <select data-testid={`lp-day-item-${idx}`} value="" onChange={(e) => add(e.target.value)} className={`${selCls} flex-1 min-w-[200px]`}>
          <option value="">+ Add work for this day…</option>
          {["lesson", "drill", "task", "checkpoint", ...UNIT_KINDS].map((k) => {
            const grp = items.filter((i) => i.kind === k);
            return grp.length ? <optgroup key={k} label={KIND[k] + (k === "lesson" || k === "drill" ? "s" : "")}>{grp.map((i) => <option key={i.id} value={i.id} disabled={day.items.some((x) => x.id === i.id)}>{i.title}</option>)}</optgroup> : null;
          })}
        </select>
      </div>
      {day.items.length === 0 ? <p className="text-xs text-amber-300/80" data-testid={`lp-day-empty-${idx}`}>Pick at least one lesson, drill, Block Task, Checkpoint or Unit Test/Task.</p> : (
        <div className="flex flex-wrap gap-1.5" data-testid={`lp-day-items-${idx}`}>
          {day.items.map((it) => (
            <span key={it.id} className="inline-flex items-center gap-1 rounded-full bg-[#22D3EE]/10 border border-[#22D3EE]/30 px-2.5 py-1 text-xs text-slate-100">
              {itemLabel(it)}
              <button type="button" data-testid={`lp-item-remove-${idx}-${it.id}`} onClick={() => onChange({ ...day, items: day.items.filter((x) => x.id !== it.id) })} className="text-slate-400 hover:text-[#E11D48]"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ScheduleCard({ days, setDays, catalog, onGenerate, generating }) {
  const ready = days.length > 0 && days.every((d) => d.items.length > 0);
  const addDay = () => setDays((ds) => [...ds, newDay(ds.length ? nextSchoolDay(ds[ds.length - 1].date) : toISO(new Date()))]);
  return (
    <div className="hq-glass rounded-2xl p-4 space-y-3" data-testid="lp-schedule-card">
      <div>
        <p className="text-sm font-medium text-slate-200 flex items-center gap-2"><CalendarRange className="w-4 h-4 text-[#22D3EE]" /> Weekly Schedule</p>
        <p className="text-xs text-muted-foreground mt-1">Each school day gets its own work. Add a lesson for teaching, a drill or Block Task for skills practice, the Checkpoint for block test day, or pick the <span className="text-slate-300">Unit Tests &amp; Tasks</span> block for Mid-Unit and End of Unit tests and tasks — mix as many as you like per day.</p>
      </div>
      {days.length === 0 && <p className="text-sm text-slate-400" data-testid="lp-schedule-empty">Pick a week date range above to create the days, or add one manually.</p>}
      {days.map((d, i) => (
        <DayRow key={d.id} day={d} idx={i} catalog={catalog}
          onChange={(nd) => setDays((ds) => ds.map((x, j) => (j === i ? nd : x)))}
          onRemove={() => setDays((ds) => ds.filter((_, j) => j !== i))} />
      ))}
      <Button data-testid="lp-add-day" onClick={addDay} variant="outline" className="w-full border-white/15" disabled={days.length >= 10}><Plus className="w-4 h-4 mr-2" /> Add day</Button>
      <Button data-testid="lp-generate-btn" onClick={onGenerate} disabled={!ready || generating} className="w-full bg-emerald-500 text-[#04121f] hover:bg-emerald-400">
        {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Drafting {days.length} day{days.length > 1 ? "s" : ""}…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate {days.length} day{days.length !== 1 ? "s" : ""} of plans</>}
      </Button>
    </div>
  );
}
